import { useEffect, useMemo, useRef, useState } from "react";
import "./style.scss";
import { NavLink } from "react-router-dom";
import { Nav, NavItem } from "reactstrap";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import axios from "axios";
import Points from "@/containers/Layout/sidebar/Points";
import AwardClaimNotification from "@/containers/Participant/ClaimAwards/components/AwardClaimNotification";

import { REDEEM_PATH } from '@/containers/Participant/GcmShared/routes';

// On wide screens the open menu pushes the page right (see style.scss) instead of
// covering it, so the page stays scrollable. Keep in sync with the SCSS breakpoint.
const PUSH_LAYOUT_QUERY = "(min-width: 992px)";
const isPushLayout = () =>
  typeof window !== "undefined" && window.matchMedia?.(PUSH_LAYOUT_QUERY).matches;

const SlideOutMenu = ({ isFixed, program, auth }) => {
  const { t } = useTranslation();

  const [isMenuOpen, setMenuOpen] = useState(Boolean(isFixed));
  const [iframeItems, setIframeItems] = useState([]);
  const [mediaItems, setMediaItems] = useState([]);
  const [claimAwardCounts, setClaimAwardCounts] = useState(0);
  const triggerRef = useRef(null);
  const drawerRef = useRef(null);
  const wasMenuOpenRef = useRef(Boolean(isFixed));
  const drawerIdRef = useRef(`participant-slideout-menu-${Math.random().toString(36).slice(2, 10)}`);
  const bodyStyleRef = useRef({
    bodyOverflow: "",
    bodyPaddingRight: "",
    htmlOverflow: "",
  });

  const toggleMenu = () => { if (!isFixed) setMenuOpen((prevState) => !prevState); };
  const closeMenu = () => { if (!isFixed) setMenuOpen(false); };

  useEffect(() => {
    if (isFixed) {
      setMenuOpen(true);
    }
  }, [isFixed]);

  useEffect(() => {
    if (!program?.id || !program?.organization_id) {
      setIframeItems([]);
      setMediaItems([]);
      return;
    }

    const loadMediaTypes = async () => {
      try {
        const res = await axios.get(
          `/organization/${program.organization_id}/program/${program.id}/digital-media-type`
        );

        if (!res.data.length) {
          setIframeItems([]);
          setMediaItems([]);
          return;
        }

        const nextMedia = [];
        const nextIframes = [];

        res.data.forEach((row) => {
          if (row.is_menu_item === 1) {
            nextIframes.push({
              id: row.program_media_type_id,
              text: row.name,
              to: `/participant/iframe/${row.program_media_type_id}`,
              section: "content",
            });
            return;
          }

          nextMedia.push({
            id: row.program_media_type_id,
            text: row.name,
            to: `/participant/media/${row.program_media_type_id}`,
            section: "content",
          });
        });

        setIframeItems(nextIframes);
        setMediaItems(nextMedia);
      } catch (e) {
        console.error(e);
        setIframeItems([]);
        setMediaItems([]);
      }
    };

    loadMediaTypes();
  }, [program?.id, program?.organization_id]);

  useEffect(() => {
    if (program?.id && program?.organization_id && auth?.id) {
      axios
        .get(`/organization/${program.organization_id}/program/${program.id}/user/${auth.id}/claim-award?countOnly=1`)
        .then(res => { if (res.status === 200) setClaimAwardCounts(res.data); })
        .catch(console.error);
      return;
    }

    setClaimAwardCounts(0);
  }, [program?.id, program?.organization_id, auth?.id]);

  useEffect(() => {
    if (isFixed || typeof document === "undefined") {
      return undefined;
    }

    if (!isMenuOpen) {
      return undefined;
    }

    const { body, documentElement } = document;

    if (isPushLayout()) {
      body.classList.add("sw-menu-open");
      return () => body.classList.remove("sw-menu-open");
    }

    const scrollbarWidth = window.innerWidth - documentElement.clientWidth;

    bodyStyleRef.current = {
      bodyOverflow: body.style.overflow,
      bodyPaddingRight: body.style.paddingRight,
      htmlOverflow: documentElement.style.overflow,
    };

    body.classList.add("sw-menu-open");
    body.style.overflow = "hidden";
    documentElement.style.overflow = "hidden";

    if (scrollbarWidth > 0) {
      body.style.paddingRight = `${scrollbarWidth}px`;
    }

    return () => {
      body.classList.remove("sw-menu-open");
      body.style.overflow = bodyStyleRef.current.bodyOverflow;
      body.style.paddingRight = bodyStyleRef.current.bodyPaddingRight;
      documentElement.style.overflow = bodyStyleRef.current.htmlOverflow;
    };
  }, [isFixed, isMenuOpen]);

  useEffect(() => {
    if (isFixed || !isMenuOpen || typeof document === "undefined") {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeMenu();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    const focusFrame = window.requestAnimationFrame(() => {
      const focusTarget =
        drawerRef.current?.querySelector(
          ".sw-drawer__body a[href], .sw-drawer__body button:not([disabled]), .sw-drawer__body [tabindex]:not([tabindex='-1'])"
        ) ||
        drawerRef.current;

      focusTarget?.focus();
    });

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      window.cancelAnimationFrame(focusFrame);
    };
  }, [isFixed, isMenuOpen]);

  useEffect(() => {
    if (!isFixed && wasMenuOpenRef.current && !isMenuOpen) {
      triggerRef.current?.focus();
    }

    wasMenuOpenRef.current = isMenuOpen;
  }, [isFixed, isMenuOpen]);

  const earnItems = useMemo(() => {
    const items = [];

    if (program?.uses_leaderboards) {
      items.push({ to: "/participant/leaderboards", text: "leaderboards", section: "earn" });
    }
    if (program?.uses_goal_tracker) {
      items.push({ to: "/participant/my-goals", text: "my_goals", section: "earn" });
    }
    if (program?.enable_referrals) {
      items.push({ to: "/participant/referral", text: "submit_a_referral", section: "earn" });
    }
    if (program?.uses_peer2peer) {
      items.push({ to: "/participant/peer-to-peer", text: "give_an_award", section: "earn" });
    }
    if (program?.enable_how_are_you_feeling) {
      items.push({ to: "/participant/feeling", text: "how_are_you_feeling", section: "earn" });
    }

    return items;
  }, [
    program?.enable_how_are_you_feeling,
    program?.enable_referrals,
    program?.uses_goal_tracker,
    program?.uses_leaderboards,
    program?.uses_peer2peer,
  ]);

  const rewardItems = useMemo(() => {
    const items = [
      { to: REDEEM_PATH, text: "nav_redeem", section: "rewards" },
      { to: "/participant/my-points", text: "nav_rewards", section: "rewards" },
      { to: "/participant/my-gift-codes", text: "nav_gift_codes", section: "rewards" },
    ];

    if (program?.user_has_to_claim_award || claimAwardCounts > 0) {
      items.push({ to: "/participant/claim-awards", text: "Claim Awards", section: "rewards" });
    }

    return items;
  }, [program?.user_has_to_claim_award, claimAwardCounts]);

  const contentItems = useMemo(() => [...mediaItems, ...iframeItems], [mediaItems, iframeItems]);

  // Section order:
  // 1. Engagement (formerly "Earn") — feature/engagement links
  // 2. Content — client-added items, shown only when present
  // 3. Rewards — redeem/points links
  // My Account and FAQ removed — always accessible from the top nav
  const sections = useMemo(() => {
    const items = [
      { key: "earn", title: "menu_section_engagement", items: earnItems },
    ];

    if (contentItems.length > 0) {
      items.push({ key: "content", title: "menu_section_content", items: contentItems });
    }

    items.push({ key: "rewards", title: "menu_section_rewards", items: rewardItems });

    return items.filter((section) => section.items.length > 0);
  }, [contentItems, earnItems, rewardItems]);

  const renderItemLabel = (item) => {
    if (item.text === "Claim Awards") {
      if (claimAwardCounts > 0) {
        return (
          <AwardClaimNotification claimAwardCounts={claimAwardCounts}>
            {t(item.text)}
          </AwardClaimNotification>
        );
      }

      return t(item.text);
    }

    return t(item.text);
  };


  return (
    <>
      {/* ── Hamburger trigger ── */}
      {!isFixed && (
        <button
          ref={triggerRef}
          className={`sw-hamburger${isMenuOpen ? " is-active" : ""}`}
          onClick={toggleMenu}
          aria-controls={drawerIdRef.current}
          aria-expanded={isMenuOpen}
          aria-label={isMenuOpen ? "Close menu" : "Open menu"}
          type="button"
        >
          <span className="bar" />
          <span className="bar" />
          <span className="bar" />
        </button>
      )}

      {/* ── Backdrop ── */}
      {!isFixed && (
        <div className={`sw-backdrop${isMenuOpen ? ' show' : ''}`} onClick={closeMenu} />
      )}

      {/* ── Drawer ── */}
      <nav
        id={drawerIdRef.current}
        ref={drawerRef}
        className={`sw-drawer${isMenuOpen ? ' show' : ''}${isFixed ? ' fixed' : ''}`}
        aria-hidden={!isFixed && !isMenuOpen}
        aria-label={t("menu")}
        tabIndex={-1}
      >

        <div className="sw-drawer__body">
          <div className="sw-drawer__points">
            <Points />
          </div>

          {sections.map((section) => (
            <section key={section.key} className="sw-group">
              <div className="sw-group__label">{t(section.title)}</div>

              <Nav vertical className="sw-group__list">
                {section.items.map((item) => (
                  <NavItem key={item.to} className="sw-nav-item">
                    <NavLink to={item.to} onClick={closeMenu} className="sw-nav-link">
                      <span className="sw-nav-link__accent" aria-hidden="true" />
                      <span className="sw-nav-link__text">{renderItemLabel(item)}</span>
                    </NavLink>
                  </NavItem>
                ))}
              </Nav>
            </section>
          ))}
        </div>
      </nav>
    </>
  );
};

const mapStateToProps = (state) => ({
  program:      state.program,
  auth:         state.auth,
});

export default connect(mapStateToProps)(SlideOutMenu);
